<clp-toolbar [title]="('community.title' | translate)">
    <a
      [routerLink]="['/createCommunity']"
      routerLinkActive="activeClass"
      class="clp-btn clp-btn-warning clp-btn-sm inline-flex items-center gap-2">
      <i class="fa-solid fa-plus"></i>
      <span>{{ 'general.create' | translate }}</span>
    </a>
</clp-toolbar>

<div class="px-4 py-3">
  @if (communities$ | async; as communities) {
    <div class="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'community.title' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-slate-100">{{ communities.length }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'community.totalMembers' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-slate-100">{{ getTotalMembers(communities) }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'community.averageMembers' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-emerald-300">{{ getAverageMembers(communities) | number:'1.0-1' }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'community.minMembers' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-amber-300">{{ minimumMembersFilter === 'all' ? '-' : minimumMembersFilter }}</p>
      </div>
    </div>

    <div class="mb-3 rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
      <div class="grid gap-2 md:grid-cols-[minmax(0,1fr)_200px]">
        <div>
          <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'community.name' | translate }}</label>
          <div class="relative">
            <i class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500"></i>
            <input
              type="text"
              class="clp-input pl-9"
              [value]="searchTerm"
              (input)="onSearchChange($event)"
              [attr.placeholder]="'community.searchPlaceholder' | translate" />
          </div>
        </div>
        <div>
          <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'community.minMembers' | translate }}</label>
          <select class="clp-select" [value]="minimumMembersFilter" (change)="onMinimumMembersFilterChange($event)">
            <option value="all">{{ 'community.allMembersCounts' | translate }}</option>
            <option value="1">1+</option>
            <option value="5">5+</option>
            <option value="10">10+</option>
          </select>
        </div>
      </div>
    </div>

    @let filteredCommunities = getFilteredCommunities(communities);

    @if (communities.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-3 py-10 text-center">
          <i class="fa-solid fa-users text-3xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'community.noCommunities' | translate }}</p>
          <a [routerLink]="['/createCommunity']" class="clp-btn clp-btn-warning clp-btn-sm">{{ 'general.create' | translate }}</a>
        </div>
      </div>
    } @else if (filteredCommunities.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-2 py-8 text-center">
          <i class="fa-solid fa-filter-circle-xmark text-2xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'community.noMatchingCommunities' | translate }}</p>
        </div>
      </div>
    } @else {
      <div class="space-y-2">
        @for (community of filteredCommunities; track community.id ?? $index) {
          <article class="group rounded-lg border border-slate-700 bg-slate-900/55 px-3 py-2.5 shadow-sm transition hover:border-amber-400/40 hover:bg-slate-900/75">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div class="min-w-0 space-y-1">
                <h3 class="truncate text-sm font-semibold text-slate-100">{{ community.name }}</h3>
                <p class="text-xs text-slate-300">
                  {{ community.members?.length ?? 0 }} {{ 'community.members' | translate | lowercase }}
                </p>
                <p class="text-xs text-slate-400">
                  {{ 'community.subscription' | translate }}: {{ community.subscriptionName || ('community.noSubscription' | translate) }}
                </p>
                <p class="text-xs text-emerald-300">
                  {{ community.discussionGroupId ? ('community.manage.groupReady' | translate) : ('community.manage.groupPending' | translate) }}
                </p>
              </div>

              <div class="flex shrink-0 items-center gap-1.5">
                <span class="rounded-full border border-slate-600 bg-slate-700/50 px-2 py-0.5 text-[11px] font-semibold text-slate-200">
                  {{ community.members?.length ?? 0 }}
                </span>

                <button
                  class="clp-btn clp-btn-secondary clp-btn-sm"
                  (click)="viewCommunitySubscribers(community.id)"
                  [disabled]="!community.id"
                  [attr.aria-label]="'community.subscribers.view' | translate"
                  [title]="'community.subscribers.view' | translate">
                  <i class="fa-solid fa-users text-xs"></i>
                </button>

                <button
                  class="clp-btn clp-btn-warning clp-btn-sm"
                  (click)="editCommunity(community.id)"
                  [attr.aria-label]="'general.edit' | translate"
                  [title]="'general.edit' | translate">
                  <i class="fa-regular fa-pen-to-square text-xs"></i>
                </button>

                <button
                  class="clp-btn clp-btn-danger clp-btn-sm"
                  (click)="openDeleteConfirmation(community)"
                  [attr.aria-label]="'general.delete' | translate"
                  [title]="'general.delete' | translate">
                  <i class="fa-solid fa-trash-can text-xs"></i>
                </button>
              </div>
            </div>
          </article>
        }
      </div>
    }
  }
</div>

@if (communityToDelete) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      class="absolute inset-0 bg-slate-950/70"
      (click)="closeDeleteConfirmation()"
      aria-hidden="true">
    </div>

    <section class="relative w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
      <div class="mb-4 flex items-start gap-3">
        <span class="flex h-9 w-9 items-center justify-center rounded-full border border-red-500/40 bg-red-500/15 text-red-300">
          <i class="fa-solid fa-triangle-exclamation text-sm"></i>
        </span>
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-slate-100">
            {{ 'community.delete.confirmTitle' | translate }}
          </h3>
          <p class="mt-1 text-sm text-slate-300">
            {{ 'community.delete.confirmMessage' | translate:{ name: (communityToDelete.name ?? '') } }}
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="clp-btn clp-btn-secondary"
          [disabled]="deleting"
          (click)="closeDeleteConfirmation()">
          {{ 'general.cancel' | translate }}
        </button>
        <button
          type="button"
          class="clp-btn clp-btn-danger"
          [disabled]="deleting"
          (click)="confirmDeleteCommunity()">
          {{ 'general.delete' | translate }}
        </button>
      </div>
    </section>
  </div>
}
